'use client';

import {
  Box,
  Button,
  RadioCard,
  TextField,
  SystemIcon,
  Typography,
  Autocomplete,
  LegacySkillItem,
  SkillItem,
} from '@wishket/design-system';
import { ChangeEvent, FormEvent, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

import { CollectionStatus } from '@/shared/types';
import { getScrollbarStyle, scrollToElementById } from '@/shared/utils';

interface SectionProps extends PropsWithChildren {
  id?: string;
  title: string;
  isRequired?: boolean;
  onFocus?: () => void;
}

interface SelectCollectionStatusProps {
  isError?: boolean;
  status: CollectionStatus | null;
  handleStatusChange: (status: CollectionStatus) => void;
}

export const Section = ({
  id,
  title,
  children,
  isRequired,
  onFocus,
}: SectionProps) => {
  return (
    <Box className="flex flex-col gap-4" id={id} onFocus={onFocus}>
      <Typography variant="body1" className="font-medium text-w-gray-900">
        {title} {isRequired && <span className="text-primary">*</span>}
      </Typography>
      {children}
    </Box>
  );
};

export const SelectCollectionStatus = ({
  status,
  isError,
  handleStatusChange,
}: SelectCollectionStatusProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleStatusChange(e.target.value as CollectionStatus);
  };

  return (
    <Box className="flex flex-col gap-4">
      <RadioCard
        isWide
        title="공개"
        iconName="public"
        className="w-full"
        isError={isError}
        value={CollectionStatus.PUBLIC}
        description="독자들과 도움 되는 지식을 나누고 함께 공유해요."
        isSelected={status === CollectionStatus.PUBLIC}
        onChange={handleChange}
      />
      <RadioCard
        isWide
        title="비공개"
        iconName="lock"
        isError={isError}
        className="w-full"
        description="나만 알고 싶은 업무 꿀팁을 정리하고 모아둬요."
        value={CollectionStatus.PRIVATE}
        isSelected={status === CollectionStatus.PRIVATE}
        onChange={handleChange}
      />
    </Box>
  );
};

interface CollectionModalFormProps {
  type: 'create' | 'edit';
  value: {
    title: string;
    description: string;
    status: CollectionStatus | null;
  };
  onChange: {
    tag: {
      input: (value: string) => void;
      delete: (item: string) => void;
    };
    status: (status: CollectionStatus) => void;
    title: (e: ChangeEvent<HTMLInputElement>) => void;
    description: (e: ChangeEvent<HTMLInputElement>) => void;
  };
  tag: {
    query: string;
    selectedItems: string[];
    staticItems: SkillItem[];
    legacyItems: LegacySkillItem[];
    setQuery: (value: string) => void;
  };
  error: {
    title: { isError: boolean; message: string };
    status: { isError: boolean; message: string };
  };
  onClose: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

const CollectionModalForm = ({
  tag,
  type,
  error,
  value,
  onChange,
  onClose,
  onSubmit,
}: CollectionModalFormProps) => {
  const handleFocusSection = (id: string) => {
    scrollToElementById(id);
  };

  return (
    <form className="w-full" onSubmit={onSubmit}>
      <Box className="flex h-[60px] items-center justify-between border-b px-5 desktop:h-fit desktop:px-8 desktop:py-6">
        <Box className="block size-6 desktop:hidden" />
        <Typography
          variant="subTitle1"
          className="font-medium text-w-gray-900 desktop:typo-subTitle1"
        >
          {type === 'create' ? '컬렉션 만들기' : '컬렉션 수정'}
        </Typography>
        <button onClick={onClose} type="button">
          <SystemIcon name="large_delete" className="text-w-gray-600" />
        </button>
      </Box>
      <Box
        className={twMerge(
          'flex h-[calc(100dvh-142px)] w-full flex-col gap-8 overflow-y-scroll pb-10 pt-6 desktop:h-fit desktop:max-h-[490px] desktop:pb-8',
          getScrollbarStyle()
        )}
      >
        <Section title="공개 여부" isRequired>
          <SelectCollectionStatus
            status={value.status}
            isError={error.status.isError}
            handleStatusChange={onChange.status}
          />
        </Section>
        {value.status && (
          <Section
            isRequired
            title="컬렉션 제목"
            id="collection_modal_title_section"
            onFocus={() => handleFocusSection('collection_modal_title_section')}
          >
            <TextField
              maxLength={25}
              value={value.title}
              isMaxLengthVisible
              placeholder="예) 프로덕트 노하우"
              isError={error.title.isError}
              supportMessage="컬렉션을 잘 표현할 수 있는 제목을 입력해 주세요."
              errorMessage={error.title.message}
              onChange={onChange.title}
            />
          </Section>
        )}
        {value.status === CollectionStatus.PUBLIC && (
          <>
            <Section
              title="컬렉션 태그"
              id="collection_modal_tag_section"
              onFocus={() => handleFocusSection('collection_modal_tag_section')}
            >
              <Autocomplete
                leadingIcon="medium_hashtag"
                value={tag.query}
                placeholder="예) AI, 개발, 협업"
                supportingText="컬렉션 주제를 잘 보여주는 태그를 입력해 주세요."
                selectedItems={tag.selectedItems}
                skillItems={tag.staticItems || []}
                legacySkillItems={tag.legacyItems || []}
                onValueChange={tag.setQuery}
                onChipDelete={onChange.tag.delete}
                onInputItemClick={onChange.tag.input}
                onSkillItemClick={(e) => onChange.tag.input(e.value)}
                onLegacySkillItemClick={(e) => onChange.tag.input(e.value)}
              />
            </Section>
            <Section
              title="컬렉션 설명"
              id="collection_modal_description_section"
              onFocus={() =>
                handleFocusSection('collection_modal_description_section')
              }
            >
              <TextField
                maxLength={35}
                isMaxLengthVisible
                value={value.description}
                supportMessage="컬렉션을 쉽게 이해할 수 있도록 간단한 설명을 입력해 주세요."
                placeholder="예) 앱 제작을 위한 필수 참고 자료"
                onChange={onChange.description}
              />
            </Section>
          </>
        )}
      </Box>
      <Box className="fixed bottom-0 w-full justify-end border-t px-5 pb-4 pt-[15px] desktop:relative desktop:px-8 desktop:py-5">
        <Box className="flex justify-end gap-4">
          <Button
            type="button"
            variant="solid"
            className="w-full border-w-bluegray-200 bg-w-bluegray-200 focus:bg-w-bluegray-300 active:bg-w-bluegray-300 hover:border-w-bluegray-300 hover:bg-w-bluegray-300 desktop:w-[89px]"
            onClick={onClose}
          >
            닫기
          </Button>
          <Button
            type="submit"
            variant="solid"
            className="w-full desktop:w-[89px]"
          >
            {type === 'create' ? '만들기' : '수정하기'}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default CollectionModalForm;
